import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

gsap.registerPlugin(ScrollToPlugin);

const NbabGti50Container = styled.button`
  position: fixed;
  right: min(4.16vw, 106.66px);
  bottom: 58px;
  z-index: 1000;
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
  outline: none;

  &:focus-visible {
    box-shadow: 0 0 0 4px rgba(0, 149, 255, 0.4);
    border-radius: 12px;
  }
`;

const NbabGti50Icon = styled.svg`
  width: 48px;
  height: 48px;
  display: block;
  /* Para animación suave sin reflow */
  transform: scale(1) translateZ(0);
  transform-origin: center;
  will-change: transform;
`;

const highlightArrival = (el: HTMLElement) => {
  gsap.fromTo(
    el,
    { outline: '2px solid rgba(0,0,0,0)', outlineOffset: 6 },
    {
      outline: '2px solid rgba(0,0,0,0.25)',
      outlineOffset: 2,
      duration: 0.2,
      yoyo: true,
      repeat: 1,
      ease: 'power1.out',
      clearProps: 'outline,outlineOffset',
      delay: 0.05,
    },
  );
};

export const NbabGti50 = (): React.ReactElement => {
  const isAnimatingRef = useRef(false);
  const iconRef = useRef<SVGSVGElement | null>(null);
  const hasInitialScrolledRef = useRef(false);

  // ===== Hover / Touch scale (GSAP) =====
  const scaleUp = () => {
    if (!iconRef.current) return;
    gsap.to(iconRef.current, { scale: 1.5, duration: 0.2, ease: 'power2.out' });
  };

  const scaleDown = () => {
    if (!iconRef.current) return;
    gsap.to(iconRef.current, { scale: 1, duration: 0.25, ease: 'power2.out' });
  };

  const handlePointerEnter = () => scaleUp();
  const handlePointerLeave = () => scaleDown();
  const handlePointerDown = () => scaleUp();
  const handlePointerUp = () => scaleDown();
  const handlePointerCancel = () => scaleDown();

  // ===== Scroll to #formulario =====
  const scrollToTarget = () => {
    const target = document.querySelector('#formulario') as HTMLElement | null;
    if (!target || isAnimatingRef.current) return;
    isAnimatingRef.current = true;

    const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOSSafari) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        isAnimatingRef.current = false;
        highlightArrival(target);
      }, 900);
    } else {
      try {
        const offsetY = 0;
        gsap.to(window, {
          duration: 2.9,
          scrollTo: { y: target, offsetY, autoKill: true },
          ease: 'power2.out',
          onComplete: () => {
            isAnimatingRef.current = false;
            highlightArrival(target);
          },
          onInterrupt: () => {
            isAnimatingRef.current = false;
          },
        });
      } catch {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => {
          isAnimatingRef.current = false;
          highlightArrival(target);
        }, 900);
      }
    }
  };

  const handleClickNbabGti50 = (e: React.MouseEvent) => {
    e.preventDefault();
    scrollToTarget();
  };

  useEffect(() => {
    if (!hasInitialScrolledRef.current && window.location.hash === '#formulario') {
      hasInitialScrolledRef.current = true;
      setTimeout(() => scrollToTarget(), 500);
    }

    return () => {
      gsap.killTweensOf(window);
      if (iconRef.current) gsap.killTweensOf(iconRef.current);
    };
  }, []);

  return (
    <NbabGti50Container
      onClick={handleClickNbabGti50}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      aria-label="Ir al formulario"
      title="Ir al formulario"
    >
      <NbabGti50Icon
        ref={iconRef}
        width="50"
        height="50"
        viewBox="0 0 50 50"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="25" cy="25" r="24" fill="#C90010" stroke="white" strokeWidth="2" />
        <path
          d="M12.5642 21.7588V18.6802C13.0847 19.3911 13.6475 19.7466 14.2527 19.7466C14.4812 19.7466 14.6674 19.6873 14.8112 19.5688C14.9551 19.4461 15.0271 19.2938 15.0271 19.1118C15.0271 18.8494 14.8705 18.5934 14.5573 18.3438L14.1257 18.0073C13.8379 17.7915 13.5925 17.5778 13.3894 17.3662C13.1862 17.1504 13.0212 16.9303 12.8943 16.7061C12.7673 16.4775 12.6742 16.2427 12.615 16.0015C12.5557 15.7603 12.5261 15.5063 12.5261 15.2397C12.5261 14.7827 12.6319 14.3045 12.8435 13.8052C12.9493 13.5724 13.072 13.3608 13.2116 13.1704C13.3513 12.9757 13.5121 12.8044 13.6941 12.6562C14.1934 12.2331 14.7964 12.0215 15.5031 12.0215C16.1337 12.0215 16.7684 12.1886 17.4074 12.5229V15.519C17.1916 15.1847 16.9462 14.9224 16.6711 14.7319C16.396 14.5373 16.1252 14.4399 15.8586 14.4399C15.6555 14.4399 15.4883 14.4992 15.3571 14.6177C15.2217 14.7319 15.154 14.8695 15.154 15.0303C15.154 15.2926 15.3339 15.5529 15.6936 15.811L16.1125 16.1221C16.6711 16.5241 17.071 16.9367 17.3122 17.3599C17.5577 17.783 17.6804 18.2782 17.6804 18.8452C17.6804 19.8101 17.3863 20.6056 16.7981 21.2319C16.2098 21.8582 15.4651 22.1714 14.5637 22.1714C13.9416 22.1714 13.2751 22.0339 12.5642 21.7588ZM18.3251 22V12.1992H23.2445V14.5034H20.972V15.9316H23.0096V18.147H20.972V19.645H23.2445V22H18.3251ZM22.6732 9.19678L21.4354 11.4121H19.9564L20.5975 9.19678H22.6732ZM27.5109 22V12.1992H32.4303V14.5034H30.1579V15.9316H32.1954V18.147H30.1579V19.645H32.4303V22H27.5109ZM37.8103 22H33.1765V12.1992H35.8235V19.645H37.8103V22Z"
          fill="white"
        />
        <path
          d="M22.9705 32.7996V35.1411H21.1757L20.5877 39.9375H18.1328L18.7207 35.1411H17.4417L16.8537 39.9375H14.4194L15.0074 35.1411H13.4601V32.7996H15.2962L15.5334 30.85H14.0068V28.4982H15.8326L16.3793 24.0112H18.8136L18.2566 28.4982H19.5356L20.1029 24.0112H22.5476L21.9906 28.4982H23.4759V30.85H21.7018L21.4645 32.7996H22.9705ZM19.2468 30.85H17.9678L17.7305 32.7996H19.0199L19.2468 30.85ZM33.3944 39.9375H29.093V27.7556H27.3704V24.0112H33.3944V39.9375Z"
          fill="white"
        />
      </NbabGti50Icon>
    </NbabGti50Container>
  );
};
